import { AxiosRequestConfig } from 'axios';
import dayjs from 'dayjs';
import posthog from 'posthog-js';
import { wifi } from 'capacitor-jd-wifi';
import { TrackerCustomEvent } from 'common-package';
import { Device } from '@capacitor/device';
import { lanInterfaceInstance } from '@/service/common';
import { Toast } from '@/components';
import { getServerUrlFromConnection } from '@/utils';
import service from '../index';
import { IS_PROD_ENV } from '../request';
import { ERR_CODE_2_TEXT } from '../utils/errorHandler';

interface LANRequestParams {
  deviceId: string;
  serviceName: string;
  params?: { [key: string]: any };
  config?: AxiosRequestConfig<any>;
}

export interface LANCommonSuccessResponse {
  code: 0;
  message: 'ok';
}

const DATE_FORMAT = 'MM-DD HH:mm:ss';

// 机器人局域网接口
const lanInterfaceApi = {
  /**
   * 获取当前机器人任务步骤
   */
  getCurrentStep: (deviceId: string) => {
    return handleLANRequest<GetCurrentStepResponse>({ deviceId, serviceName: 'getCurrentStep' });
  },
  /**
   * 设备麦克风状态
   */
  setMicState: (deviceId: string, params: SwitchState) => {
    return handleLANRequest<LANCommonSuccessResponse>({ deviceId, serviceName: 'setMicrophone', params });
  },

  /**
   * 设备停驻
   */
  parking: (deviceId: string) => {
    return handleLANRequest<LANCommonSuccessResponse>({
      deviceId,
      serviceName: 'parking',
      config: {
        timeout: 60000,
      },
    });
  },
  /**
   * 关闭RTC
   */
  closeRtc: (deviceId: string) => {
    return handleLANRequest<LANCommonSuccessResponse>({
      deviceId,
      serviceName: 'closeRtc',
    });
  },
  /**
   * 关闭定位辅助
   */
  closePositionAssistance: (deviceId: string) => {
    return handleLANRequest<LANCommonSuccessResponse>({
      deviceId,
      serviceName: 'closePositionAssistance',
    });
  },
  /**
   * 设备取消停驻
   */
  cancelParking: (deviceId: string) => {
    return handleLANRequest<LANCommonSuccessResponse>({
      deviceId,
      serviceName: 'cancel_parking',
      config: {
        timeout: 60000,
      },
    });
  },
  /**
   * 获取按摩头状态
   */
  getCollisionCheckState: (deviceId: string) => {
    return handleLANRequest<GetMoxibustionCheckStateResponse>({ deviceId, serviceName: 'get_collision_check_state' });
  },
  /**
   * 按摩头预检
   */
  collisionPreflight: (deviceId: string) => {
    return handleLANRequest<GetMoxibustionCheckStateResponse>({
      deviceId,
      serviceName: 'collision_preflight',
      config: {
        timeout: 15000,
      },
    });
  },
  /**
   * 关闭按摩头检测
   */
  closeCollisionCheck: (deviceId: string) => {
    return handleLANRequest<LANCommonSuccessResponse>({
      deviceId,
      serviceName: 'close_collision_check',
    });
  },
  /**
   * 强制关闭按摩头检测
   */
  forceCloseCollisionCheck: (deviceId: string) => {
    return handleLANRequest<LANCommonSuccessResponse>({
      deviceId,
      serviceName: 'forceCloseCollisionCheck',
    });
  },
  /**
   * 获取当前任务
   */
  getTaskSequenceStatus: (deviceId: string) => {
    return handleLANRequest<GetTaskSequenceStatus>({
      deviceId,
      serviceName: 'getTaskSequenceStatus',
    });
  },
  /**
   * 执行下一个任务
   */
  runTask: ({ deviceId, taskId }: RunTaskParams) => {
    return handleLANRequest<LANCommonSuccessResponse>({
      deviceId,
      serviceName: 'runTask',
      params: { taskId },
    });
  },
  /**
   * 重试人体检测
   */
  retryHumanDetecting: (deviceId: string) => {
    return handleLANRequest<LANCommonSuccessResponse>({
      deviceId,
      serviceName: 'retryHumanDetecting',
    });
  },
  /**
   * 播放设备检测
   */
  speakerCheck: ({ deviceId, checkType }: SpeakerCheckParams) => {
    return handleLANRequest<LANCommonSuccessResponse>({
      deviceId,
      serviceName: 'speakerCheck',
      params: { checkType },
    });
  },
  /**
   * 麦克风检测
   */
  microphoneCheck: ({ deviceId, duration }: MicrophoneCheckParams) => {
    return handleLANRequest<LANCommonSuccessResponse>({
      deviceId,
      serviceName: 'microphoneCheck',
      params: { duration },
    });
  },
  /**
   * 一键清除语音检测
   */
  speakerClearAll: (deviceId: string) => {
    return handleLANRequest<LANCommonSuccessResponse>({
      deviceId,
      serviceName: 'speakerClearAll',
    });
  },
  /**
   * 外接设备校验
   */
  externalDevicePreflight: ({ deviceId, item_type }: ExternalDevicePreflightParams) => {
    return handleLANRequest<ExternalDevicePreflightResponse>({
      deviceId,
      serviceName: 'externalDevicePreflight',
      params: { item_type },
    });
  },
  /**
   * 退出误差排查
   */
  exitTroubleShoot: (deviceId: string) => {
    return handleLANRequest<LANCommonSuccessResponse>({
      deviceId,
      serviceName: 'exitTroubleShoot',
    });
  },
  /**
   * 结束沉浸模式按摩头实时位置展示功能
   */
  endRealTimeDisplay: (deviceId: string) => {
    return handleLANRequest<LANCommonSuccessResponse>({
      deviceId,
      serviceName: 'endRealTimeDisplay',
    });
  },
};

/**
 * 局域网接口调用
 */
const handleLANRequest = <T = any>(requestParams: LANRequestParams) => {
  const { deviceId, serviceName, params, config } = requestParams || {};

  return new Promise<T>(async (resolve, reject) => {
    try {
      // 从服务器获取当前设备的ip连接信息
      const { data: connectInfo } = await service.device.getConnectionInfo(deviceId);

      if (!connectInfo?.host) {
        const errMsg = '未获取到机器人的连接信息，请检查。';
        Toast.show(errMsg);
        reject(errMsg);
        return;
      }

      const serverURL = `${getServerUrlFromConnection(connectInfo)}/api/services`;

      if (!IS_PROD_ENV) {
        const reqTime = dayjs().format(DATE_FORMAT);
        console.log(`%c LAN-Req ${serviceName} [${reqTime}]`, 'color:  #ff21b0;', params ?? '');
      }

      const data = await lanInterfaceInstance.post(
        serverURL,
        { service: serviceName, ...(params ? { params } : {}) },
        config || null
      );

      if (!IS_PROD_ENV) {
        const resTime = dayjs().format(DATE_FORMAT);
        console.log(`%c LAN-Res ${serviceName} [${resTime}]`, 'color:  #ff21b0', data?.data ?? '');
        //@ts-ignore
        posthog.sessionRecording?.log({
          type: 'lan-interface-log',
          url: serviceName,
          time: resTime,
          data: data?.data ?? '',
        });
      }
      resolve(data?.data);
    } catch (e) {
      Toast.show(ERR_CODE_2_TEXT[e?.code] || e?.message || '无法连接到机器人，请检查网络连接状态');
      console.error('局域网接口错误', serviceName, e);

      Device.getInfo()
        .then(async info => {
          if (info.platform === 'android') {
            const ssidInfo = await wifi.getSSID();
            const ipInfo = await wifi.getIP();

            posthog.capture(TrackerCustomEvent.LanConnectError, {
              et: 'monitor',
              c1: ssidInfo?.ssid,
              c2: ipInfo?.ip,
            });
          }
        })
        .catch(e => {
          console.log('获取设备或wifi信息失败', e);
        });

      reject(e);
    }
  });
};

export default lanInterfaceApi;
